import { useAuth } from '@tom-ui/utils'
import {
  FormControl,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  EquipmentPlannedDto,
  HatchCoverMoveDirection,
  HatchCoverMoveDto,
} from '@operations/app/api'
import { HatchCoverFormData } from '@operations/stores/HatchCoverMoveStore'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface IProps {
  formId: string
  vesselVisitId: number
  craneItems: EquipmentPlannedDto[]
  hatchCoverMove?: HatchCoverMoveDto
  onSubmit: (formData: HatchCoverFormData) => Promise<void>
}

export const HatchCoverMoveItemForm = observer(
  ({ formId, vesselVisitId, craneItems, onSubmit, hatchCoverMove }: IProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { user } = useAuth()

    const defaultValues: HatchCoverFormData = useMemo(() => {
      return {
        id: hatchCoverMove?.id ?? undefined,
        direction: hatchCoverMove?.direction ?? HatchCoverMoveDirection.Open,
        equipmentId: hatchCoverMove?.equipmentId ?? '',
        carrierVisitId: hatchCoverMove?.carrierVisitId ?? vesselVisitId,
        operator: hatchCoverMove?.operator ?? user?.name ?? '',
        at: hatchCoverMove?.at ? dayjs(hatchCoverMove?.at).toDate().toLocaleString() : undefined,
      }
    }, [
      hatchCoverMove?.at,
      hatchCoverMove?.carrierVisitId,
      hatchCoverMove?.direction,
      hatchCoverMove?.equipmentId,
      hatchCoverMove?.id,
      hatchCoverMove?.operator,
      user?.name,
      vesselVisitId,
    ])

    const {
      control,
      handleSubmit,
      formState: { errors },
    } = useForm<HatchCoverFormData>({
      values: defaultValues,
    })

    return (
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack gap={theme.customSpacing.m}>
          <Stack gap={theme.customSpacing.m} direction='row'>
            <FormGroup sx={{ gap: theme.customSpacing.xs, width: '100%' }}>
              <Typography variant='subtitle2'>{t('equipment', 'Equipment')}</Typography>
              <FormControl fullWidth required>
                <InputLabel>{t('crane', 'Crane')}</InputLabel>
                <Controller
                  control={control}
                  name='equipmentId'
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      label={t('crane', 'Crane')}
                      onChange={event => {
                        field.onChange(event.target.value)
                      }}
                      error={!!errors.equipmentId}
                      data-cy='hc-crane-select'
                    >
                      {craneItems.map(crane => (
                        <MenuItem
                          key={crane.id}
                          value={crane.id}
                          data-cy={`hc-crane-select-${crane.name}`}
                        >
                          {crane.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors['equipmentId'] && (
                  <FormHelperText error>{t('fieldIsRequired', 'Field is required')}</FormHelperText>
                )}
              </FormControl>
            </FormGroup>

            <FormGroup sx={{ gap: theme.customSpacing.xs, width: '100%' }}>
              <Typography variant='subtitle2'>{t('direction', 'Direction')}</Typography>
              <FormControl fullWidth required>
                <InputLabel>{t('direction', 'Direction')}</InputLabel>
                <Controller
                  control={control}
                  name='direction'
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      label={t('direction', 'Direction')}
                      onChange={event => {
                        field.onChange(event.target.value)
                      }}
                      data-cy={`hc-direction-select`}
                    >
                      {Object.values(HatchCoverMoveDirection).map(d => (
                        <MenuItem key={d} value={d} data-cy={`hc-direction-select-${d}`}>
                          {t(d, d)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </FormGroup>
          </Stack>

          {hatchCoverMove && (
            <Stack gap={theme.customSpacing.m} direction='row'>
              <FormGroup sx={{ gap: theme.customSpacing.xs, width: '100%' }}>
                <Typography variant='subtitle2'>{t('date', 'Date')}</Typography>
                <FormControl fullWidth required>
                  <Controller
                    control={control}
                    name='at'
                    render={({ field }) => (
                      <TextField
                        disabled={true}
                        {...field}
                        fullWidth
                        label={t('date', 'Date')}
                      ></TextField>
                    )}
                  />
                </FormControl>
              </FormGroup>

              <FormGroup sx={{ gap: theme.customSpacing.xs, width: '100%' }}>
                <Typography variant='subtitle2'>{t('user', 'User')}</Typography>
                <FormControl fullWidth required>
                  <Controller
                    control={control}
                    name='operator'
                    render={({ field }) => (
                      <TextField
                        disabled={true}
                        {...field}
                        fullWidth
                        label={t('user', 'User')}
                      ></TextField>
                    )}
                  />
                </FormControl>
              </FormGroup>
            </Stack>
          )}
        </Stack>
      </form>
    )
  },
)
